import { useState, useRef, useEffect, useMemo } from "react";
import QRCodeStyling, { type DotType, type CornerSquareType, type CornerDotType, type Options } from "qr-code-styling";
import { ToolContainer, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { X } from "lucide-react";
import { toast } from "sonner";
import { contrastWarning } from "@/lib/qrHelpers";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `A styled QR code maker: choose dot and corner shapes, solid or gradient colours and a centre logo so the code matches your brand. Download as PNG (up to 2,000 px) or SVG.`,
  how: `Enter your content, pick a dot style and corner style, set the colours (turn on the gradient for a two-colour blend) and optionally add a logo. Error correction is raised to the strongest level automatically when a logo is present. Download as PNG or SVG.`,
  tip: `Keep a dark code on a light background, even with a gradient: both gradient colours should stay dark. Rounded and dotted styles look great but scan slightly less forgivingly than square ones, so test with your phone before printing.`,
};

const DOT_STYLES: { value: DotType; label: string }[] = [
  { value: "square", label: "Square" }, { value: "rounded", label: "Rounded" }, { value: "dots", label: "Dots" },
  { value: "classy", label: "Classy" }, { value: "classy-rounded", label: "Classy rounded" }, { value: "extra-rounded", label: "Extra rounded" },
];
const EYE_STYLES: { value: CornerSquareType; label: string }[] = [
  { value: "square", label: "Square" }, { value: "extra-rounded", label: "Rounded" }, { value: "dot", label: "Circle" },
];
const EYE_DOT_STYLES: { value: CornerDotType; label: string }[] = [
  { value: "square", label: "Square" }, { value: "dot", label: "Dot" },
];

function Opt<T extends string>({ label, value, onChange, items }: { label: string; value: T; onChange: (v: T) => void; items: { value: T; label: string }[] }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={v => onChange(v as T)}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>{items.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function CustomQr() {
  const [text, setText] = useState("https://freedailypro.com");
  const [dotStyle, setDotStyle] = useState<DotType>("rounded");
  const [eyeStyle, setEyeStyle] = useState<CornerSquareType>("extra-rounded");
  const [eyeDotStyle, setEyeDotStyle] = useState<CornerDotType>("dot");
  const [fg, setFg] = useState("#4338ca");
  const [gradient, setGradient] = useState(false);
  const [fg2, setFg2] = useState("#0f172a");
  const [bg, setBg] = useState("#ffffff");
  const [size, setSize] = useState("1000");
  const [logo, setLogo] = useState<string | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<QRCodeStyling | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const options = useMemo(
    () => (px: number): Options => ({
      width: px,
      height: px,
      type: "canvas",
      data: text || " ",
      margin: Math.round(px * 0.04),
      qrOptions: { errorCorrectionLevel: logo ? "H" : "M" },
      dotsOptions: gradient
        ? { type: dotStyle, gradient: { type: "linear", rotation: Math.PI / 4, colorStops: [{ offset: 0, color: fg }, { offset: 1, color: fg2 }] } }
        : { type: dotStyle, color: fg },
      cornersSquareOptions: { type: eyeStyle, color: fg },
      cornersDotOptions: { type: eyeDotStyle, color: gradient ? fg2 : fg },
      backgroundOptions: { color: bg },
      image: logo ?? undefined,
      imageOptions: { hideBackgroundDots: true, imageSize: 0.25, margin: 4, crossOrigin: "anonymous" },
    }),
    [text, dotStyle, eyeStyle, eyeDotStyle, fg, fg2, gradient, bg, logo],
  );

  useEffect(() => {
    if (!hostRef.current) return;
    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(options(300));
      qrRef.current.append(hostRef.current);
    } else {
      qrRef.current.update(options(300));
    }
  }, [options]);

  const warnings = [contrastWarning(fg, bg), gradient ? contrastWarning(fg2, bg) : null].filter(Boolean) as string[];
  const warning = warnings[0] ?? null;

  const onLogo = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file for the logo."); return; }
    const r = new FileReader();
    r.onload = () => setLogo(String(r.result));
    r.readAsDataURL(file);
  };

  const exportAs = async (ext: "png" | "svg") => {
    if (!text.trim()) { toast.error("Enter some content first"); return; }
    try {
      const px = Number(size);
      const q = new QRCodeStyling({ ...options(px), type: ext === "svg" ? "svg" : "canvas" });
      const raw = await q.getRawData(ext);
      if (!raw) throw new Error("empty");
      downloadBlob(raw as Blob, `custom-qr.${ext}`);
    } catch { toast.error("Couldn't create the file"); }
  };

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div><Label>Content</Label><Input aria-label="Content" value={text} onChange={e => setText(e.target.value)} data-testid="cq-text" /></div>
          <div className="grid grid-cols-2 gap-3">
            <Opt label="Dot style" value={dotStyle} onChange={setDotStyle} items={DOT_STYLES} />
            <Opt label="Corner style" value={eyeStyle} onChange={setEyeStyle} items={EYE_STYLES} />
            <Opt label="Corner centre" value={eyeDotStyle} onChange={setEyeDotStyle} items={EYE_DOT_STYLES} />
            <div>
              <Label>Download size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["300", "500", "1000", "1500", "2000"].map(s => <SelectItem key={s} value={s}>{s} px</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{gradient ? "Gradient start" : "Code colour"}</Label><Input type="color" value={fg} onChange={e => setFg(e.target.value)} aria-label="Code colour" /></div>
            <div><Label>Background</Label><Input type="color" value={bg} onChange={e => setBg(e.target.value)} aria-label="Background colour" /></div>
            {gradient && <div><Label>Gradient end</Label><Input type="color" value={fg2} onChange={e => setFg2(e.target.value)} aria-label="Gradient end colour" /></div>}
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={gradient} onChange={e => setGradient(e.target.checked)} data-testid="cq-gradient" /> Use a two-colour gradient</label>
          {warning && <NoticeBox className="text-sm" role="alert" testId="cq-contrast-warning">{warning}</NoticeBox>}

          <div>
            <Label>Logo (optional)</Label>
            {logo ? (
              <div className="flex items-center gap-2 mt-1.5">
                <img src={logo} alt="Logo preview" className="h-9 w-9 rounded object-contain border bg-white" />
                <span className="text-xs text-muted-foreground flex-1">Logo added. Error correction raised automatically; the dots behind it are cleared.</span>
                <Button size="icon" variant="ghost" onClick={() => { setLogo(null); if (logoInputRef.current) logoInputRef.current.value = ""; }} className="h-7 w-7 shrink-0"><X className="h-3.5 w-3.5" /></Button>
              </div>
            ) : (
              <Input ref={logoInputRef} type="file" accept="image/*" onChange={e => onLogo(e.target.files?.[0])} className="mt-1.5" data-testid="cq-logo" />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <DownloadButton onClick={() => exportAs("png")} label="Download PNG" disabled={!text.trim()} />
            <DownloadButton onClick={() => exportAs("svg")} label="Download SVG" disabled={!text.trim()} />
          </div>
        </div>
        <div className="flex items-center justify-center p-4 border rounded-xl bg-card min-h-[320px]">
          <div ref={hostRef} data-testid="cq-host" />
        </div>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
